import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import { 
  Bell, CheckCircle, FileText, Upload, Clock, CheckCheck, Eye, EyeOff
} from 'lucide-react';
import { notificationsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isEmployee = user?.role === 'employee';

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await notificationsApi.getAll();
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
      setTotalCount(res.data.total_count || res.data.notifications?.length || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRead = async (notif) => {
    try {
      if (notif.is_read) {
        await notificationsApi.markUnread(notif.notification_id);
        setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, is_read: false } : n));
        setUnreadCount(prev => prev + 1);
        toast.success('Marked as unread');
      } else {
        await notificationsApi.markRead(notif.notification_id);
        setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
        toast.success('Marked as read');
      }
      // Trigger sidebar bell refresh
      window.dispatchEvent(new Event('notif-updated'));
    } catch (error) {
      toast.error('Failed to update notification');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
      window.dispatchEvent(new Event('notif-updated'));
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleViewAndRead = async (notif) => {
    if (!notif.is_read) {
      try {
        await notificationsApi.markRead(notif.notification_id);
        setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
        window.dispatchEvent(new Event('notif-updated'));
      } catch (e) {}
    }
  };

  const getNotifIcon = (type) => {
    switch (type) {
      case 'document_reuploaded': return <Upload className="w-5 h-5 text-blue-500" />;
      case 'document_rejected': return <FileText className="w-5 h-5 text-red-500" />;
      case 'application_updated': return <CheckCircle className="w-5 h-5 text-green-500" />;
      default: return <Bell className="w-5 h-5 text-[#FF5A36]" />;
    }
  };

  const getAppLink = (notif) => {
    if (isAdmin) return `/admin/applications/${notif.application_id}`;
    if (isEmployee) return `/employee/applications/${notif.application_id}`;
    return `/applications/${notif.application_id}`;
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar />
      
      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="notifications-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Notifications
            </h1>
            <p className="text-[#4A5568] mt-1">
              {totalCount > 0 
                ? `${totalCount} notification${totalCount > 1 ? 's' : ''}${unreadCount > 0 ? ` (${unreadCount} unread)` : ' — all read'}`
                : 'No notifications yet'}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="outline" 
              className="border-[#D1CBBF]"
              onClick={handleMarkAllRead}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" /> Mark All Read
            </Button>
          )}
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-[#D1CBBF] animate-pulse">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell className="w-16 h-16 text-[#D1CBBF] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#0A1E40] mb-2">No notifications</h3>
            <p className="text-[#4A5568]">You'll see notifications here when there's activity on your applications.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notif) => (
              <div 
                key={notif.notification_id}
                className={`bg-white rounded-xl border overflow-hidden transition-all ${
                  notif.is_read ? 'border-[#D1CBBF]' : 'border-[#FF5A36]/30 shadow-sm bg-[#FF5A36]/[0.02]'
                }`}
                data-testid={`notif-${notif.notification_id}`}
              >
                <div className="flex items-start gap-4 p-5">
                  {/* Unread dot */}
                  <div className="w-3 flex items-center justify-center mt-2 shrink-0">
                    {!notif.is_read && (
                      <div className="w-2.5 h-2.5 rounded-full bg-[#FF5A36]" />
                    )}
                  </div>
                  
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    notif.type === 'document_reuploaded' ? 'bg-blue-50' : 'bg-[#F5F0E6]'
                  }`}>
                    {getNotifIcon(notif.type)}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${notif.is_read ? 'text-[#4A5568] font-normal' : 'text-[#0A1E40] font-bold'}`}>
                      {notif.message}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-[#9CA3AF] flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {timeAgo(notif.created_at)}
                      </span>
                      <span className="text-xs text-[#9CA3AF]">
                        {new Date(notif.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        notif.is_read ? 'bg-gray-100 text-gray-500' : 'bg-[#FF5A36]/10 text-[#FF5A36]'
                      }`}>
                        {notif.is_read ? 'Read' : 'Unread'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Link to={getAppLink(notif)} onClick={() => handleViewAndRead(notif)}>
                      <Button size="sm" className="bg-[#FF5A36] hover:bg-[#E0492B] text-white text-xs" data-testid={`notif-view-${notif.notification_id}`}>
                        View
                      </Button>
                    </Link>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleToggleRead(notif)}
                      title={notif.is_read ? 'Mark as unread' : 'Mark as read'}
                      className="text-[#4A5568] hover:text-[#0A1E40]"
                      data-testid={`notif-toggle-${notif.notification_id}`}
                    >
                      {notif.is_read ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default NotificationsPage;
